import React, { useEffect, useMemo, useState } from 'react'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import MenuGuideButton from '../components/MenuGuideButton'

const UPLOAD_SOURCE_LABELS = {
    GENERIC: '일반 파일',
    PUBLIC_PROFILE: '모바일명함',
    PUBLIC_CARD_ORG_LOGO: '모바일명함',
    PUBLIC_LANDING: '카테고리 랜딩',
    BANNER: '운영 배너',
    REQUEST_ATTACHMENT: '설계의뢰',
    NOTICE_ATTACHMENT: '공지사항',
    INSURER_NEWSLETTER: '보험사 소식지',
    CLAIM_BRAND_LOGO: '보험금청구',
    CLAIM_DOCUMENT: '보험금청구',
    CLAIM_PUBLIC_DOCUMENT: '보험금청구',
    CLAIM_FAX_RENDERED_PDF: '보험금청구',
    SYSTEM_NOTICE: '운영공지',
    EXTERNAL_PROGRAM: '외부 프로그램',
    COVERAGE_ANALYSIS: '보장분석',
    EDUCATION_MATERIAL: '교육센터',
    GEMINI_LIBRARY: '이미지 자료실',
    MONTHLY_BRIEF_CARD: '이달의 이슈',
    INSURER_NEWSLETTER_AI: '보험사 소식지',
    SALES_IMAGE: '영업 이미지',
    SALES_IMAGE_JOB: '영업 이미지',
    CLAIM_SIGNATURE: '서명',
    USER_SIGNATURE: '서명',
    CUSTOMER_REF_BUSINESS_CARD: '고객관리',
    CUSTOMER_REGISTRATION_CONSENT: '고객등록',
    POLICY_DELIVERY_BOOK_TEMPLATE: '증권전달북',
    UNKNOWN: '출처 미상',
}

function normalizeUploadSourceKey(source) {
    const code = String(source || '').trim()
    if (!code) return 'GENERIC'
    const idx = code.indexOf(':')
    return (idx > 0 ? code.slice(0, idx) : code).toUpperCase()
}

function getUploadSourceLabel(source) {
    return UPLOAD_SOURCE_LABELS[normalizeUploadSourceKey(source)] || '파일'
}

function getUploadSourceKey(file) {
    return normalizeUploadSourceKey(file?.uploadSourceFilterKey || file?.uploadSource)
}

function getUploadSourceDisplay(file) {
    return file?.uploadSourceLabel || getUploadSourceLabel(file?.uploadSource)
}

function getUploadSourceDetail(file) {
    return file?.uploadSourceDetail || getUploadSourceLabel(file?.uploadSource)
}

function fileRowDomId(fileId) {
    return `file-row-${encodeURIComponent(String(fileId || '')).replace(/%/g, '_')}`
}

function fileApiPathId(file) {
    return encodeURIComponent(canonicalStoredFileIdForApiPath(file?.id || ''))
}

function scrollToFileRow(fileId) {
    if (typeof window === 'undefined') return
    window.setTimeout(() => {
        const el = document.getElementById(fileRowDomId(fileId))
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 0)
}

export default function FilesPage() {
    const { hasAnyRole } = useAuth()
    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [shareOpen, setShareOpen] = useState(false)
    const [shareFile, setShareFile] = useState(null)
    const [expiresAt, setExpiresAt] = useState('')
    const [shareLink, setShareLink] = useState('')
    const [shareCreating, setShareCreating] = useState(false)
    const [sourceFilter, setSourceFilter] = useState('ALL')
    const [focusedFileId, setFocusedFileId] = useState(() => {
        if (typeof window === 'undefined') return ''
        return new URLSearchParams(window.location.search).get('fileId') || ''
    })
    const [uploading, setUploading] = useState(false)
    const { show } = useToast()

    useEffect(() => {
        let ignore = false
        async function load() {
            setLoading(true)
            setError(null)
            try {
                const res = await api.get('/files')
                const data = res?.data?.data ?? res?.data ?? []
                if (!ignore) setFiles(Array.isArray(data) ? data : [])
            } catch (e) {
                if (!ignore) setError('파일 목록을 불러오지 못했습니다')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        load()
        return () => { ignore = true }
    }, [])

    const onPreview = async (file) => {
        try {
            const res = await api.get(`/files/${fileApiPathId(file)}/preview`, { responseType: 'blob' })
            const url = URL.createObjectURL(res.data)
            window.open(url, '_blank')
            URL.revokeObjectURL(url)
        } catch { }
    }

    const onOpenShare = (file) => {
        setShareFile(file)
        setShareLink('')
        setExpiresAt('')
        setShareOpen(true)
    }

    const closeShare = () => {
        setShareOpen(false)
        setShareFile(null)
        setExpiresAt('')
        setShareLink('')
        setShareCreating(false)
    }

    const onUpload = async (e) => {
        const f = e.target.files?.[0]
        if (!f) return
        setUploading(true)
        try {
            const form = new FormData()
            form.append('file', f)
            await api.post('/files', form, { headers: { 'Content-Type': 'multipart/form-data' } })
            show('업로드 완료', 'success')
            const res = await api.get('/files')
            const data = res?.data?.data ?? res?.data ?? []
            setFiles(Array.isArray(data) ? data : [])
        } catch (err) {
            show('업로드 실패: ' + (err.response?.data?.message || err.message), 'error')
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    const onDelete = async (file) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        try {
            await api.delete(`/files/${fileApiPathId(file)}`)
            show('삭제되었습니다', 'success')
            setFiles(prev => prev.filter(x => x.id !== file.id))
        } catch (err) {
            const status = err?.response?.status
            const message = err?.response?.data?.message || err.message
            // 참조중 파일(409)은 운영자에게 강제삭제 옵션 제공
            if (status === 409 && isOperator) {
                const force = window.confirm(
                    '참조 중인 파일입니다.\n연결된 참조를 해제하고 강제 삭제하시겠습니까?'
                )
                if (!force) {
                    show('삭제가 취소되었습니다', 'info')
                    return
                }
                try {
                    await api.delete(`/files/${fileApiPathId(file)}/force`)
                    show('강제 삭제되었습니다', 'success')
                    setFiles(prev => prev.filter(x => x.id !== file.id))
                    return
                } catch (forceErr) {
                    show('강제 삭제 실패: ' + (forceErr?.response?.data?.message || forceErr.message), 'error')
                    return
                }
            }
            show('삭제 실패: ' + message, 'error')
        }
    }

    const onCreateShare = async (e) => {
        e.preventDefault()
        setShareCreating(true)
        try {
            const res = await api.post(`/files/${fileApiPathId(shareFile)}/share`, {
                expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null
            })
            const link = res?.data?.data?.url ?? res?.data?.url
            if (link) {
                setShareLink(link)
                await copyText(link)
                show('공유 링크가 복사되었습니다', 'success')
            }
        } catch (err) {
            show('공유 링크 생성 실패: ' + (err.response?.data?.message || err.message), 'error')
        } finally {
            setShareCreating(false)
        }
    }

    const formatFileSize = (bytes) => {
        if (!bytes) return '-'
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
    }

    const sourceOptions = useMemo(() => {
        const map = new Map()
        for (const file of files) {
            const key = getUploadSourceKey(file)
            if (!map.has(key)) {
                map.set(key, getUploadSourceDisplay(file))
            }
        }
        return Array.from(map, ([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, 'ko'))
    }, [files])

    const filteredFiles = useMemo(() => {
        if (sourceFilter === 'ALL') return files
        return files.filter(file => getUploadSourceKey(file) === sourceFilter)
    }, [files, sourceFilter])

    useEffect(() => {
        if (!focusedFileId || files.length === 0) return
        if (!files.some(file => String(file.id) === String(focusedFileId))) return
        setSourceFilter('ALL')
        scrollToFileRow(focusedFileId)
    }, [focusedFileId, files])

    const fileLocationUrl = (file) => {
        if (typeof window === 'undefined') return `/files?fileId=${encodeURIComponent(file?.id || '')}`
        const url = new URL('/files', window.location.origin)
        url.searchParams.set('fileId', file?.id || '')
        return url.toString()
    }

    const copyText = async (text) => {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return
        }
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
    }

    const onCopyFileLocation = async (file) => {
        const url = fileLocationUrl(file)
        try {
            await copyText(url)
            show('파일 위치 링크가 복사되었습니다', 'success')
        } catch (err) {
            show('링크 복사 실패: ' + (err?.message || '클립보드를 사용할 수 없습니다'), 'error')
        }
        if (typeof window !== 'undefined') {
            const next = new URL(url)
            window.history.replaceState(null, '', `${next.pathname}${next.search}`)
        }
        setSourceFilter('ALL')
        setFocusedFileId(file.id)
        scrollToFileRow(file.id)
    }

    const header = (
        <div className="flex flex-wrap items-center gap-3 border-b bg-white px-4 py-3">
            <select
                className="rounded border px-3 py-2 text-sm"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
            >
                <option value="ALL">전체 출처</option>
                {sourceOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <div className="text-sm text-slate-500">{filteredFiles.length} / {files.length}건</div>
        </div>
    )

    const table = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">이름</th>
                    <th className="px-4 py-2 text-left">크기</th>
                    <th className="px-4 py-2 text-left">업로드 출처</th>
                    <th className="px-4 py-2 text-left">업로더</th>
                    <th className="px-4 py-2 text-left">업로드</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {filteredFiles.length === 0 && (
                    <tr>
                        <td className="px-4 py-8 text-center text-slate-500" colSpan={6}>표시할 파일이 없습니다</td>
                    </tr>
                )}
                {filteredFiles.map(f => (
                    <tr
                        key={f.id}
                        id={fileRowDomId(f.id)}
                        className={`border-t hover:bg-slate-50 ${String(focusedFileId) === String(f.id) ? 'bg-sky-50 ring-1 ring-brand-sky/40' : ''}`}
                    >
                        <td className="px-4 py-2">{f.name}</td>
                        <td className="px-4 py-2">{f.sizeLabel || formatFileSize(f.size) || '-'}</td>
                        <td className="px-4 py-2">
                            <div className="font-medium text-slate-800">{getUploadSourceDisplay(f)}</div>
                            <div className="mt-0.5 text-xs text-slate-500">{getUploadSourceDetail(f)}</div>
                        </td>
                        <td className="px-4 py-2">
                            {f.uploadedBy ? (
                                <span>{f.uploadedBy.name || f.uploadedBy.email || '-'}</span>
                            ) : (
                                <span className="text-slate-400">-</span>
                            )}
                        </td>
                        <td className="px-4 py-2">{formatDateTimeMinute(f.createdAt)}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                            <button className="text-brand-navy hover:underline mr-3" onClick={() => onPreview(f)}>미리보기</button>
                            <button className="text-brand-navy hover:underline mr-3" onClick={() => onCopyFileLocation(f)}>링크복사</button>
                            <button className="text-brand-navy hover:underline mr-3" onClick={() => onOpenShare(f)}>공유 링크</button>
                            <button className="text-red-600 hover:underline" onClick={() => onDelete(f)}>삭제</button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    return (
        <section>
            <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold">파일</h1>
                    <MenuGuideButton slug="file-management" title="파일관리 가이드" />
                </div>
                <label className="text-sm">
                    <span className={`rounded border px-3 py-2 inline-block cursor-pointer ${uploading ? 'opacity-60' : ''}`}>{uploading ? '업로드 중…' : '+ 파일 업로드'}</span>
                    <input type="file" className="hidden" onChange={onUpload} disabled={uploading} />
                </label>
            </div>

            <ListContainer
                loading={loading}
                error={error}
                onRetry={async () => {
                    try {
                        const res = await api.get('/files')
                        const data = res?.data?.data ?? res?.data ?? []
                        setFiles(Array.isArray(data) ? data : [])
                    } catch (e) {
                        setError('파일 목록을 불러오지 못했습니다')
                    }
                }}
                header={header}
                table={table}
                footer={null}
            />

            {shareOpen && shareFile && (
                <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={closeShare}>
                    <form onSubmit={onCreateShare} className="w-full max-w-sm rounded bg-white p-6 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-semibold">공유 링크 생성</h2>
                        <div className="text-sm">파일: <span className="font-medium">{shareFile.name}</span></div>
                        <div>
                            <label className="block text-sm">만료일(선택)</label>
                            <input type="datetime-local" className="mt-1 w-full rounded border px-3 py-2" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                        </div>
                        {shareLink ? (
                            <div>
                                <label className="block text-sm">공유 링크</label>
                                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" readOnly value={shareLink} onFocus={(e) => e.target.select()} />
                            </div>
                        ) : null}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={closeShare}>닫기</button>
                            {shareLink ? (
                                <button type="button" className="rounded border px-3 py-2 text-sm" onClick={async () => {
                                    await copyText(shareLink)
                                    show('공유 링크가 복사되었습니다', 'success')
                                }}>복사</button>
                            ) : null}
                            {shareLink ? (
                                <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => window.open(shareLink, '_blank', 'noopener,noreferrer')}>열기</button>
                            ) : null}
                            <button type="submit" className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-60" disabled={shareCreating}>
                                {shareCreating ? '생성 중…' : '생성'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </section>
    )
}


